import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/utils/i18n';
import { TrendingUp, TrendingDown, Wallet, Brain, Target, Plus, Zap, Shield, BarChart2, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import MobileNav from '@/components/MobileNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MARKET_MOODS_DATA = [
  { key: 'bullish', pct: 68, barColor: 'bg-green-400' },
  { key: 'neutral', pct: 18, barColor: 'bg-yellow-400' },
  { key: 'bearish', pct: 14, barColor: 'bg-red-400' },
];

const TODAY_OPPORTUNITIES = [
  { symbol: 'NVDA', signal: 'Breakout Setup', type: 'stock' },
  { symbol: 'ETH', signal: 'Accumulation Zone', type: 'crypto' },
  { symbol: 'PLTR', signal: 'Momentum Signal', type: 'stock' },
  { symbol: 'SOL', signal: 'Volume Spike', type: 'crypto' },
];

function Dashboard() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [portfolio, setPortfolio] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [stats, setStats] = useState({ totalValue: 0, totalProfitLoss: 0, totalProfitLossPct: 0 });
  const [sentiment, setSentiment] = useState({ bullish: 62, neutral: 22, bearish: 16, summary: '', topSectors: [] });
  const [sentimentLoading, setSentimentLoading] = useState(false);

  useEffect(() => { fetchPortfolio(); fetchRecommendations(); fetchSentiment(); }, []);

  const fetchSentiment = async () => {
    setSentimentLoading(true);
    try {
      const res = await axios.get(`${API}/market/sentiment`, { headers: { Authorization: `Bearer ${token}` } });
      setSentiment(res.data);
    } catch { /* use defaults */ }
    finally { setSentimentLoading(false); }
  };

  const fetchPortfolio = async () => {
    try {
      const res = await axios.get(`${API}/portfolio`, { headers: { Authorization: `Bearer ${token}` } });
      setPortfolio(res.data);
      const tv = res.data.reduce((s, a) => s + a.current_price * a.quantity, 0);
      const tpl = res.data.reduce((s, a) => s + a.profit_loss, 0);
      const tc = res.data.reduce((s, a) => s + a.buy_price * a.quantity, 0);
      setStats({ totalValue: tv, totalProfitLoss: tpl, totalProfitLossPct: tc > 0 ? (tpl / tc) * 100 : 0 });
    } catch {}
  };

  const fetchRecommendations = async () => {
    try {
      const res = await axios.get(`${API}/recommendations`, { headers: { Authorization: `Bearer ${token}` } });
      setRecommendations(res.data.slice(0, 1));
    } catch {}
  };

  const mockChartData = [
    { name: 'Mon', value: 45000 }, { name: 'Tue', value: 47000 },
    { name: 'Wed', value: 46500 }, { name: 'Thu', value: 49000 },
    { name: 'Fri', value: 48200 }, { name: 'Sat', value: 50300 },
    { name: 'Sun', value: stats.totalValue || 52000 },
  ];

  const moodLabels = { bullish: t('bullish'), neutral: t('neutral'), bearish: t('bearish') };
  const MARKET_MOODS_LIVE = [
    { key: 'bullish', pct: sentiment.bullish, barColor: 'bg-green-400' },
    { key: 'neutral', pct: sentiment.neutral, barColor: 'bg-yellow-400' },
    { key: 'bearish', pct: sentiment.bearish, barColor: 'bg-red-400' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <MobileNav currentPage="dashboard" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">

        {/* Welcome */}
        <div className="mb-6 animate-slide-up">
          <h1 className="text-2xl sm:text-4xl font-bold mb-1">
            {t('welcomeBack')}, <span className="gradient-text">{user?.name}</span>
          </h1>
          <p className="text-gray-400 text-sm">
            {t('aiDashboard')} • {new Date().toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Stats */}
        <div data-testid="stats-grid" className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {[
            { testid: 'stat-total-value', icon: <Wallet className="w-4 h-4 text-[#ff6b35]" />, label: t('portfolioValue'), value: `$${stats.totalValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}`, color: 'text-white' },
            { testid: 'stat-profit-loss', icon: <TrendingUp className="w-4 h-4 text-[#ff6b35]" />, label: t('totalPL'), value: `${stats.totalProfitLoss >= 0 ? '+' : ''}$${Math.abs(stats.totalProfitLoss).toLocaleString('en-US', { maximumFractionDigits: 0 })} (${stats.totalProfitLossPct.toFixed(1)}%)`, color: stats.totalProfitLoss >= 0 ? 'text-green-400' : 'text-red-400' },
            { testid: 'stat-assets', icon: <Target className="w-4 h-4 text-[#ff6b35]" />, label: t('assets'), value: portfolio.length, color: 'text-white' },
            { testid: 'stat-risk', icon: <Shield className="w-4 h-4 text-blue-400" />, label: t('riskLevel'), value: t('medium'), color: 'text-blue-400' },
          ].map(s => (
            <div key={s.testid} data-testid={s.testid} className="glass rounded-2xl p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-2">{s.icon}<span className="text-gray-400 text-xs">{s.label}</span></div>
              <div className={`text-xl sm:text-2xl font-bold ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* AI Sentiment + Opportunities */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="glass rounded-2xl p-5 border border-[#ff6b35]/10">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-[#ff6b35]/10 flex items-center justify-center">
                <Activity className="w-4 h-4 text-[#ff6b35]" />
              </div>
              <div>
                <h3 className="font-bold text-sm">{t('aiMarketSentiment')}</h3>
                <p className="text-xs text-gray-500">{t('updatedByAI')} • Today</p>
              </div>
            </div>
            <div className="space-y-3">
              {MARKET_MOODS_LIVE.map(m => (
                <div key={m.key}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-semibold text-gray-300">{moodLabels[m.key]}</span>
                    <span className="text-sm font-bold text-gray-300">{m.pct}%</span>
                  </div>
                  <div className="w-full bg-gray-800 rounded-full h-2">
                    <div className={`${m.barColor} h-2 rounded-full`} style={{ width: `${m.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-4 pt-3 border-t border-gray-800">
              {sentiment.summary || (language === 'fr' ? 'Sentiment global haussier. Tech & crypto montrent de forts signaux.' : language === 'es' ? 'Sentimiento general alcista.' : 'Overall sentiment is Bullish.')}
              {sentiment.prices?.BTC && (
                <span className="block mt-1 text-gray-600">
                  BTC: ${sentiment.prices.BTC.toLocaleString('en-US', {maximumFractionDigits: 0})} • ETH: ${sentiment.prices?.ETH?.toLocaleString('en-US', {maximumFractionDigits: 0})}
                </span>
              )}
            </p>
          </div>

          <div className="glass rounded-2xl p-5 border border-[#ff6b35]/10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#ff6b35]/10 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-[#ff6b35]" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">{t('todaysOpportunities')}</h3>
                  <p className="text-xs text-gray-500">{t('aiDetectedSignals')}</p>
                </div>
              </div>
              <Button onClick={() => navigate('/gems')} variant="ghost" size="sm" className="text-[#ff6b35] text-xs">
                {t('viewAll')} →
              </Button>
            </div>
            <div className="space-y-2">
              {TODAY_OPPORTUNITIES.map((opp, i) => (
                <div key={i} onClick={() => navigate('/gems')} className="flex items-center justify-between p-2.5 bg-[#0a0a0a]/60 rounded-xl hover:bg-[#0a0a0a] transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${opp.type === 'stock' ? 'bg-blue-500/20 text-blue-400' : 'bg-orange-500/20 text-orange-400'}`}>
                      {opp.symbol.substring(0, 2)}
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{opp.symbol}</div>
                      <div className="text-xs text-gray-500">{opp.signal}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-green-400 text-xs font-semibold">
                    <TrendingUp className="w-3 h-3" /> Signal
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Chart + AI Insight */}
        <div className="grid lg:grid-cols-2 gap-4 mb-6">
          <div data-testid="performance-chart" className="glass rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold">{t('portfolioPerformance')}</h2>
              <span className="text-xs text-gray-500">7 {language === 'fr' ? 'jours' : language === 'es' ? 'días' : 'days'}</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={mockChartData}>
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff6b35" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#ff6b35" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
                <XAxis dataKey="name" stroke="#555" tick={{ fontSize: 11 }} />
                <YAxis stroke="#555" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #ff6b35', borderRadius: '8px', fontSize: '12px' }} />
                <Area type="monotone" dataKey="value" stroke="#ff6b35" fillOpacity={1} fill="url(#colorValue)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div data-testid="latest-recommendation" className="glass rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-[#ff6b35]" />
                <h2 className="font-bold">{t('latestAIInsight')}</h2>
              </div>
              <Button onClick={() => navigate('/recommendations')} variant="ghost" size="sm" className="text-[#ff6b35] text-xs">
                {t('allInsights')}
              </Button>
            </div>
            {recommendations.length > 0 ? (
              <div>
                <div className="text-xs text-gray-500 mb-3 flex items-center gap-1">
                  <Zap className="w-3 h-3 text-[#ff6b35]" />{recommendations[0].model_used}
                </div>
                <p className="text-gray-300 text-sm leading-relaxed line-clamp-6">{recommendations[0].content}</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <Brain className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                <p className="text-gray-500 text-sm mb-4">{t('noAIInsights')}</p>
                <Button onClick={() => navigate('/recommendations')} size="sm" className="bg-[#ff6b35] text-black font-semibold">
                  {t('generateNowBtn')}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Recent Assets */}
        <div data-testid="recent-assets" className="glass rounded-2xl p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-bold flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-[#ff6b35]" />{t('myPortfolio')}
            </h2>
            <Button onClick={() => navigate('/portfolio')} size="sm" className="bg-[#ff6b35] text-black font-semibold text-xs">
              <Plus className="w-3 h-3 mr-1" />{t('manage')}
            </Button>
          </div>
          {portfolio.length > 0 ? (
            <div className="space-y-2">
              {portfolio.slice(0, 5).map((asset, idx) => (
                <div key={asset.id} data-testid={`asset-item-${idx}`} className="flex justify-between items-center p-3 bg-[#0a0a0a]/50 rounded-xl hover:bg-[#0a0a0a] transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#ff6b35]/10 flex items-center justify-center">
                      <span className="font-bold text-[#ff6b35] text-xs">{asset.symbol.substring(0, 2)}</span>
                    </div>
                    <div>
                      <div className="font-semibold text-sm">{asset.symbol}</div>
                      <div className="text-xs text-gray-500">{asset.name}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-sm">${asset.current_price.toFixed(2)}</div>
                    <div className={`text-xs flex items-center gap-0.5 justify-end ${asset.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {asset.profit_loss >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {asset.profit_loss_percentage.toFixed(2)}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10">
              <Wallet className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500 text-sm mb-4">{t('noAssetsYet')}</p>
              <Button onClick={() => navigate('/portfolio')} size="sm" className="bg-[#ff6b35] text-black font-semibold">
                <Plus className="w-3 h-3 mr-1" />{t('addFirstAsset')}
              </Button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-600 mt-6">{t('aiDisclaimer')}</p>
      </div>
    </div>
  );
}

export default Dashboard;
